import { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import formatDateShort from "@/lib/dateFormatter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Activity,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  User,
  Clock,
  FileText,
  Shield,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

export default function AdminActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  });
  const [filters, setFilters] = useState({
    user_filter: "",
    role_filter: "",
    module_filter: "",
    action_filter: "",
    date_from: "",
    date_to: "",
  });
  const [filterOptions, setFilterOptions] = useState({
    users: [],
    roles: [],
    modules: [],
    actions: [],
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchLogs();
    fetchFilterOptions();
  }, [pagination.page, filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        page: pagination.page,
        per_page: pagination.per_page,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)),
      });

      const res = await axios.get(`${API}/activity-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setLogs(res.data.data);
      setPagination((prev) => ({
        ...prev,
        total: res.data.total,
        total_pages: res.data.total_pages,
      }));
    } catch (error) {
      toast.error("Failed to fetch activity logs");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/activity-logs/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFilterOptions(res.data);
    } catch (error) {
      console.error("Failed to fetch filter options");
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case "login":
        return "bg-green-100 text-green-800";
      case "logout":
        return "bg-gray-100 text-gray-800";
      case "create":
        return "bg-blue-100 text-blue-800";
      case "update":
        return "bg-yellow-100 text-yellow-800";
      case "delete":
        return "bg-red-100 text-red-800";
      default:
        return "bg-slate-100 text-slate-800";
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "super_admin":
        return "bg-purple-100 text-purple-800";
      case "admin":
        return "bg-blue-100 text-blue-800";
      case "staff":
        return "bg-green-100 text-green-800";
      case "tech":
        return "bg-orange-100 text-orange-800";
      case "client":
        return "bg-slate-100 text-slate-800";
      case "system":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-slate-100 text-slate-800";
    }
  };

  const handleClearFilters = () => {
    setFilters({
      user_filter: "",
      role_filter: "",
      module_filter: "",
      action_filter: "",
      date_from: "",
      date_to: "",
    });
  };

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-64px)] flex flex-col overflow-hidden p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1
              className="text-2xl sm:text-3xl font-bold text-slate-900"
              data-testid="activity-logs-title"
            >
              Activity Logs
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Monitor all system activities and user actions
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card className="mb-6" data-testid="filters-panel">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Filters</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    User
                  </label>
                  <Select
                    value={filters.user_filter}
                    onValueChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        user_filter: value === "all" ? "" : value,
                      }))
                    }
                  >
                    <SelectTrigger className="mt-1" data-testid="filter-user">
                      <SelectValue placeholder="All Users" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      {filterOptions.users.map((user) => (
                        <SelectItem key={user} value={user}>
                          {user}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Role
                  </label>
                  <Select
                    value={filters.role_filter}
                    onValueChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        role_filter: value === "all" ? "" : value,
                      }))
                    }
                  >
                    <SelectTrigger className="mt-1" data-testid="filter-role">
                      <SelectValue placeholder="All Roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {filterOptions.roles.map((role) => (
                        <SelectItem key={role} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Module
                  </label>
                  <Select
                    value={filters.module_filter}
                    onValueChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        module_filter: value === "all" ? "" : value,
                      }))
                    }
                  >
                    <SelectTrigger className="mt-1" data-testid="filter-module">
                      <SelectValue placeholder="All Modules" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Modules</SelectItem>
                      {filterOptions.modules.map((module) => (
                        <SelectItem key={module} value={module}>
                          {module}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Action
                  </label>
                  <Select
                    value={filters.action_filter}
                    onValueChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        action_filter: value === "all" ? "" : value,
                      }))
                    }
                  >
                    <SelectTrigger className="mt-1" data-testid="filter-action">
                      <SelectValue placeholder="All Actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Actions</SelectItem>
                      {filterOptions.actions.map((action) => (
                        <SelectItem key={action} value={action}>
                          {action}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    From Date
                  </label>
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        date_from: e.target.value,
                      }))
                    }
                    className="mt-1"
                    data-testid="filter-date-from"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    To Date
                  </label>
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        date_to: e.target.value,
                      }))
                    }
                    className="mt-1"
                    data-testid="filter-date-to"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  data-testid="clear-filters-btn"
                >
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{pagination.total}</p>
                  <p className="text-xs text-slate-500">Total Logs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <User className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {filterOptions.users.length}
                  </p>
                  <p className="text-xs text-slate-500">Active Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {filterOptions.modules.length}
                  </p>
                  <p className="text-xs text-slate-500">Modules</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Shield className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {filterOptions.actions.length}
                  </p>
                  <p className="text-xs text-slate-500">Action Types</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Logs Table */}
        <Card>
          <CardContent className="p-0 overflow-hidden flex flex-col h-[calc(100vh-320px)]">
            <div className="overflow-auto flex-1">
              <table className="w-full" data-testid="activity-logs-table">
                <thead className="border-b border-slate-200 sticky top-0 bg-blue-100 z-10">
                  <tr className="border-b bg-slate-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Date/Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Role
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Action
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Module
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Record
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-8 text-center text-slate-500"
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : logs.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-8 text-center text-slate-500"
                      >
                        No activity logs found
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr
                        key={log.id}
                        className="hover:bg-slate-50"
                        data-testid={`log-row-${log.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-700 whitespace-nowrap">
                              {formatDateShort(log.created_at)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-slate-900">
                            {log.user_name}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(log.user_role)}`}
                          >
                            {log.user_role}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${getActionColor(log.action)}`}
                          >
                            {log.action}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-700 capitalize">
                            {log.module}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-700">
                            {log.record_name || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className="text-sm text-slate-500 truncate max-w-xs block"
                            title={log.details}
                          >
                            {log.details || "-"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-20 block bg-[#f9f9f9] lg:hidden">
              <span> </span>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="px-4 py-3 border-t items-center justify-between sticky bottom-0 hidden lg:flex">
                <p className="text-sm text-slate-600">
                  Showing {(pagination.page - 1) * pagination.per_page + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.per_page,
                    pagination.total,
                  )}{" "}
                  of {pagination.total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === 1}
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-600">
                    Page {pagination.page} of {pagination.total_pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === pagination.total_pages}
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    data-testid="next-page-btn"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            <div className="fixed bottom-0 left-0 w-full bg-white border-t shadow-md z-40 block lg:hidden">
              {pagination.total_pages > 1 && (
                <div className="border-t bg-white px-4 py-3 flex items-center justify-between sticky bottom-0">
                  <p className="text-sm text-slate-600">
                    Showing {(pagination.page - 1) * pagination.per_page + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.per_page,
                      pagination.total,
                    )}{" "}
                    of {pagination.total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === 1}
                      onClick={() =>
                        setPagination((prev) => ({
                          ...prev,
                          page: prev.page - 1,
                        }))
                      }
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {pagination.page} of {pagination.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === pagination.total_pages}
                      onClick={() =>
                        setPagination((prev) => ({
                          ...prev,
                          page: prev.page + 1,
                        }))
                      }
                      data-testid="next-page-btn"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
